import { lazy } from "react";
/**
 * Dairy, web half: the Run dairy workspace, the Lactation records on a cow, the morning worklists,
 * fresh-check and mastitis forms built for the parlor, and settings. A beef ranch sees none of it:
 * the section and actions need a dairy record or the dairy preset.
 */
import { defineWebModule } from "../../web/module";
import type { AnimalDetail } from "../../contract";
import { dairyPresetCached, isFresh, ranchToday } from "./web/common";
import { liveDim, type DairyFacet } from "./shared/lactation";
import "./web/dairy.css";

// Pages load when opened, so the app starts fast on a phone with weak signal.
const LactationSection = lazy(() => import("./web/LactationSection").then((m) => ({ default: m.LactationSection })));
const DairySettings = lazy(() => import("./web/Settings").then((m) => ({ default: m.DairySettings })));
const DairyWorkspace = lazy(() => import("./web/Workspace").then((m) => ({ default: m.DairyWorkspace })));
const DryOffJob = lazy(() => import("./web/jobs").then((m) => ({ default: m.DryOffJob })));
const FreshCheckForm = lazy(() => import("./web/forms").then((m) => ({ default: m.FreshCheckForm })));
const FreshRoundJob = lazy(() => import("./web/jobs").then((m) => ({ default: m.FreshRoundJob })));
const MastitisForm = lazy(() => import("./web/forms").then((m) => ({ default: m.MastitisForm })));
const TestImportJob = lazy(() => import("./web/jobs").then((m) => ({ default: m.TestImportJob })));

const dairyOf = (a: AnimalDetail) => (a.facets.dairy as DairyFacet | undefined) ?? null;
const today = ranchToday;
const milking = (a: AnimalDetail) => dairyOf(a)?.status === "milking";

export default defineWebModule({
  id: "dairy",
  events: {
    "dairy.fresh_check": { Form: FreshCheckForm },
    "dairy.mastitis": { Form: MastitisForm },
  },
  animalSections: [
    { id: "lactation", label: "Lactation", order: 22, show: (a) => !!a.facets.dairy, Component: LactationSection },
  ],
  animalActions: [
    { id: "dairy-fresh-check", label: "Fresh check", icon: "thermometer", eventType: "dairy.fresh_check", priority: 70, when: (a) => milking(a) && isFresh(liveDim(dairyOf(a), today())) },
    { id: "dairy-mastitis", label: "Mastitis", icon: "water", eventType: "dairy.mastitis", priority: 45, when: (a) => !!dairyOf(a) && dairyOf(a)!.status !== "heifer", data: () => ({ discard: true }) },
    { id: "dairy-test-day", label: "Test day", icon: "lab", eventType: "dairy.test_day", priority: 38, when: milking },
    { id: "dairy-milk-weight", label: "Milk weight", icon: "milk", eventType: "dairy.milk_weight", priority: 36, when: milking, data: () => ({ period: "day" }) },
    { id: "dairy-dry-off", label: "Dry off", icon: "moon", eventType: "dairy.dry_off", priority: 34, when: (a) => milking(a) && (liveDim(dairyOf(a), today()) ?? 0) >= 150 },
    { id: "dairy-milk-out", label: "Milk dumped", icon: "milk", eventType: "dairy.milk_disposition", priority: 30, when: milking, data: () => ({ disposition: "discarded" }) },
    {
      id: "dairy-lactation-start", label: "Lactation start", icon: "milk", eventType: "dairy.lactation_start", priority: 20, data: () => ({ cause: "calving" }),
      when: (a) => a.sex === "female" && (dairyOf(a)?.status === "dry" || (!dairyOf(a) && dairyPresetCached() && a.class !== "heifer_calf")),
    },
  ],
  workspaces: { dairy: DairyWorkspace },
  jobs: {
    "dairy.fresh_round": FreshRoundJob,
    "dairy.dry_off_list": DryOffJob,
    "dairy.test_import": TestImportJob,
  },
  Settings: DairySettings,
});
